import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const VARIANTS = {
  info: {
    color: '#00a7e1',
    Icon: InfoOutlinedIcon,
  },
  warning: {
    color: '#c2410c',
    Icon: WarningAmberOutlinedIcon,
  },
  error: {
    color: '#dc2626',
    Icon: ErrorOutlineIcon,
  },
  confirm: {
    color: '#00a7e1',
    Icon: HelpOutlineIcon,
  },
};

const MessageDialog = ({
  open,
  variant = 'info',
  title,
  message,
  items,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  onClose,
  onConfirm,
}) => {
  const tone = VARIANTS[variant] || VARIANTS.info;
  const Icon = tone.Icon;
  const lines = Array.isArray(message) ? message.filter(Boolean) : null;
  const handleConfirm = onConfirm || onClose;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      aria-labelledby='app-message-dialog-title'
      maxWidth='xs'
      fullWidth
      sx={{ zIndex: 22000 }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      <DialogTitle
        id='app-message-dialog-title'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          m: 0,
          px: 2.5,
          py: 1.75,
          background: tone.color,
          color: '#ffffff',
          fontSize: '1.05rem',
          fontWeight: 700,
          lineHeight: 1.3,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
        {title}
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: '20px !important', pb: 2, background: '#ffffff' }}>
        {lines ? (
          <ul style={{ margin: 0, paddingLeft: 18, color: '#334155' }}>
            {lines.map((line) => (
              <li key={line} style={{ marginBottom: 6 }}>{line}</li>
            ))}
          </ul>
        ) : (
          <div style={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.5 }}>{message}</div>
        )}
        {items && items.length > 0 ? (
          <ul style={{ margin: '12px 0 0', paddingLeft: 18, color: '#0f172a' }}>
            {items.map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </ul>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1,
          background: '#f4f8fb',
          borderTop: '1px solid #d7e8f2',
        }}
      >
        {variant === 'confirm' ? (
          <Button
            onClick={onClose}
            variant='outlined'
            sx={{
              color: '#0077a3',
              borderColor: '#00a7e1',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': { background: '#e6f6fc', borderColor: '#0077a3' },
            }}
          >
            {cancelLabel}
          </Button>
        ) : null}
        <Button
          onClick={handleConfirm}
          variant='contained'
          disableElevation
          autoFocus
          sx={{
            background: tone.color,
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            '&:hover': { background: tone.color, filter: 'brightness(0.92)' },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const InfoDialog = (props) => <MessageDialog {...props} variant='info' />;
export const WarningDialog = (props) => <MessageDialog {...props} variant='warning' />;
export const ErrorDialog = (props) => <MessageDialog {...props} variant='error' />;
export const ConfirmDialog = (props) => <MessageDialog {...props} variant='confirm' />;

export default MessageDialog;



// WEBPACK FOOTER //
// ./src/components/common/message-dialog/MessageDialog.js